import React from "react";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  apiGetReportDownload,
  apiDownloadFile,
} from "../../resource/index.js";
import Storage from "../../common/lib/storage";
import DateRange from "../../common/lib/dateRange";
import * as types from '../../constants/actionTypes';
import useAPIError from "../../common/hooks/useAPIError";
import theme from '../../constants/theme';

const useStyles = makeStyles(() => ({
  root: {
    width: (dynamicWidth) => dynamicWidth - theme.spacing(39).replace('px', ''),
    padding: theme.spacing(2),
  },
  downloadSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  alert: {
    marginTop: theme.spacing(2),
    maxWidth: 500,
  },
}));

export default function ReportDownloadReport() {
  const { reportDownloadState } = useSelector((state) => state.report);
  const dispatch = useDispatch();
  const setReportDownloadState = (val) => dispatch({ type: types.SET_REPORT_DOWNLOAD_STATE, value: val });
  const { addError } = useAPIError();
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [messageType, setMessageType] = React.useState("info");
  const regulator = Storage.getRegulator() ?? "";
  const { filters } = useSelector(state => state.report);

  const downloadFile = (file) => {
    apiDownloadFile(file)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.log(err);
        setMessage("Failed to download file");
        setMessageType("error");
      });
  };

  const handleDownload = () => {
    if (DateRange.FindRangeError(filters.fromDate, filters.toDate)) {
      addError(DateRange.FindRangeError(filters.fromDate, filters.toDate));
      return;
    }

    const fromDate = new Date(filters.fromDate);
    const toDate = new Date(filters.toDate);
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      setMessage("Invalid date range");
      setMessageType("error");
      return;
    }
    const inclusiveDays = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
    if (inclusiveDays > 31) {
      setMessage("Date range must not exceed 31 days");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    apiGetReportDownload({
      regulator: regulator,
      from: filters.fromDate || "",
      to: filters.toDate || "",
      reportType: reportDownloadState.reportType,
      levelId: filters.levelId || "",
      showAll: filters.showAll,
    })
      .then((res) => {
        setLoading(false);
        if (res.data.status === "success") {
          if (res.data.data.file) {
            downloadFile(res.data.data.file);
            setMessage("Report generated successfully");
            setMessageType("success");
          } else {
            setMessage("No data found for the selected criteria");
            setMessageType("warning");
          }
        } else {
          setMessage("Failed to generate report");
          setMessageType("error");
        }
      })
      .catch((err) => {
        setLoading(false);
        const errMsg = err?.response?.data?.message || "Failed to generate report";
        setMessage(errMsg);
        setMessageType("error");
      });
  };

  const [dynamicWidth, setDynamicWidth] = React.useState(window.innerWidth);
  const classes = useStyles(dynamicWidth);

  React.useEffect(() => {
    const handleResize = () => setDynamicWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={classes.root}>
      <Typography variant="h6" gutterBottom>
        Report Download (MT + FCS Merged)
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Download a merged report combining MT and FCS data. Date range limited to 31 days.
      </Typography>
      <div className={classes.downloadSection}>
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
          onClick={handleDownload}
          disabled={loading}
          className={classes.button}
        >
          {loading ? "Generating..." : "Download Report"}
        </Button>
        {message && (
          <Alert severity={messageType} className={classes.alert}>
            {message}
          </Alert>
        )}
      </div>
    </div>
  );
}
